// src/components/doctor/ConsultHeader.jsx
//
// Tier 1 — the sticky header of the appointment page: who, what and when, the
// status (with queue token and minutes in consultation), the autosave state, the
// ⋯ admin menu and the ONE primary action, "Sign & complete".

import React, { useEffect, useRef, useState } from 'react';
import avatarTint from '../../utils/avatarTint';
import {
  statusMeta, initials, fmtShortDay, fmtTime, humanise, minutesSince, CLOSED_STATUSES,
} from '../../utils/appointmentDisplay';

export function SaveIndicator({ state, savedAt, onRetry }) {
  if (state === 'error') {
    return (
      <span role="status" className="consult-save consult-save--bad">
        <span>Not saved</span>
        <button type="button" className="btn btn-secondary btn-sm" onClick={onRetry}>Retry</button>
      </span>
    );
  }
  if (state === 'saving') return <span role="status" className="consult-save consult-save--busy">Saving…</span>;
  if (state === 'dirty') return <span role="status" className="consult-save consult-save--busy">Unsaved changes</span>;
  const at = savedAt ? ` ${savedAt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}` : '';
  return <span role="status" className="consult-save consult-save--ok"><span aria-hidden="true">✓</span> Saved{at}</span>;
}

/** The ⋯ menu: appointment details, patient record, and the close-out actions. */
function AdminMenu({ status, onDetails, onRecord, onClose }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const btnRef = useRef(null);
  const closed = CLOSED_STATUSES.includes(status);

  useEffect(() => {
    if (!open) return undefined;
    wrapRef.current?.querySelector('[role="menuitem"]')?.focus();
    const onDoc = e => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onKey = e => {
      if (e.key === 'Escape') { setOpen(false); btnRef.current?.focus(); return; }
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const items = Array.from(wrapRef.current.querySelectorAll('[role="menuitem"]'));
        const i = items.indexOf(document.activeElement);
        items[(i + (e.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length]?.focus();
      }
      if (e.key === 'Tab') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);

  // Focus goes back to ⋯ first, so a dialog the item opens captures it as its opener and returns here.
  const run = fn => () => { setOpen(false); btnRef.current?.focus(); fn(); };
  return (
    <div className="menu-wrap" ref={wrapRef}>
      <button type="button" ref={btnRef} className="btn btn-ghost btn-icon" aria-label="More actions" aria-haspopup="menu"
        aria-expanded={open} onClick={() => setOpen(o => !o)}>⋯</button>
      {open && (
        <div className="menu menu--down" role="menu" aria-label="Appointment actions">
          <button type="button" role="menuitem" className="menu-item" onClick={run(onDetails)}>Appointment details</button>
          {onRecord && <button type="button" role="menuitem" className="menu-item" onClick={run(onRecord)}>View patient record</button>}
          {!closed && (
            <>
              <button type="button" role="menuitem" className="menu-item" onClick={run(() => onClose('COMPLETED'))}>Mark complete without a summary</button>
              <button type="button" role="menuitem" className="menu-item menu-item--danger" onClick={run(() => onClose('NO_SHOW'))}>Patient didn&apos;t show</button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function ConsultHeader({
  appointment, patientName, age, gender, token, consultationStartedAt,
  signed = false, saveState, savedAt, onRetrySave, canSign = false, signing = false, onSign, showWork = false,
  onDetails, onRecord, onCloseVisit, onContext, contextOpen = false,
}) {
  const [now, setNow] = useState(() => new Date());
  const inRoom = appointment.status === 'IN_CONSULTATION';
  useEffect(() => {
    if (!inRoom) return undefined;
    const t = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(t);
  }, [inRoom]);

  const meta = statusMeta(appointment.status);
  const mins = inRoom ? minutesSince(consultationStartedAt, now) : null;
  const [first = '', last = ''] = (patientName || '').split(' ');
  const ageSex = [age != null ? age : null, gender ? String(gender).charAt(0).toUpperCase() : null].filter(v => v != null).join(' ');
  const when = [humanise(appointment.appointmentType), [fmtShortDay(appointment.appointmentDate), fmtTime(appointment.appointmentTime)].filter(Boolean).join(', '),
    appointment.doctorName, appointment.location].filter(v => v && v !== '—').join(' · ');
  const badgeText = [token, meta.label, mins != null ? `${mins} min` : null].filter(Boolean).join(' · ');

  return (
    <header className="card consult-header fade-in-up" data-testid="consult-header">
      <div className={`avatar ${avatarTint(first)}`} aria-hidden="true">{initials(first, last)}</div>
      <div className="consult-header__who">
        <h1 className="consult-header__name">{patientName || 'Patient'}{ageSex && <small>{ageSex}</small>}</h1>
        <div className="consult-header__sub">{when}</div>
      </div>
      <span className={`badge ${meta.cls}`} data-testid="status-badge">{badgeText}</span>
      <div className="consult-header__actions">
        {showWork && !signed && <SaveIndicator state={saveState} savedAt={savedAt} onRetry={onRetrySave} />}
        {showWork && signed && <span className="badge badge-active">Signed</span>}
        {onContext && (
          <button type="button" className="btn btn-secondary btn-sm consult-ctxbtn" aria-expanded={contextOpen} onClick={onContext}>Context</button>
        )}
        <AdminMenu status={appointment.status} onDetails={onDetails} onRecord={onRecord} onClose={onCloseVisit} />
        {showWork && !signed && (
          <button type="button" className="btn btn-accent" disabled={!canSign || signing} aria-disabled={saveState === 'saving' ? true : undefined} onClick={onSign}
            aria-describedby={canSign ? undefined : 'consult-sign-hint'}>Sign &amp; complete</button>
        )}
      </div>
      {showWork && !signed && !canSign && <div className="consult-signhint" id="consult-sign-hint">Add a diagnosis to enable signing</div>}
    </header>
  );
}
